/**
 * Tools for undo.
 * @module undo
 * @license MIT
 * @copyright 2011 Jeff Schiller
 */
import * as draw from './draw';
import * as hstry from './history';
import { NS } from '@/common/namespaces';
import { getRotationAngle, getBBox as utilsGetBBox, isNullish, setHref, getStrokedBBoxDefaultVisible, getHref } from './utilities';
import { isGecko } from '@/common/browser';
import { transformPoint, transformListToTransform } from './math';
import { getTransformList } from './svgtransformlist';
import { getUnlockedElements } from './layer-tools';
import SvgCanvas from './svgcanvas';
import { InsertElementCommand, MoveElementCommand, ChangeElementCommand, CommandAttributes, CommandAttributeValue, replaceChildren, serializeChildren } from './undocommands';
import { svgEditServiceInstance } from '@/services';
import { SvgAnnotations } from "@/components/svg-annotations";

const {
    UndoManager, HistoryEventTypes
} = hstry;

interface EditorContext {
    call(ev, arg): any;
    restoreRefElems(elem: SVGElement);
    getSVGContent()
}

interface LayoutLine {
    index: number;
    text: string;
    x: number;
    y: number;
}

interface TextLayoutResult {
    svg?: {
        textAnchor?: string;
        lines?: LayoutLine[];
    };
}

let undoContext_ = null;

/**
* @function module:undo.init
* @param {module:undo.undoContext} undoContext
* @returns {void}
*/
export const init = function (undoContext) {
    undoContext_ = undoContext;
};

export const getUndoManager = function (canvas: SvgCanvas) {
    return new UndoManager({
        getCanvas() { return canvas },

        /**
         * @param {string} eventType One of the HistoryEvent types
         * @param {module:history.HistoryCommand} cmd Fulfills the HistoryCommand interface
         * @fires module:undo.SvgCanvas#event:changed
         * @returns {void}
         */
        handleHistoryEvent(eventType, cmd) {
            const EventTypes = HistoryEventTypes;
            // TODO: handle setBlurOffsets.
            if (eventType === EventTypes.BEFORE_UNAPPLY || eventType === EventTypes.BEFORE_APPLY) {
                undoContext_.getCanvas().clearSelection();
            } else if (eventType === EventTypes.AFTER_APPLY || eventType === EventTypes.AFTER_UNAPPLY) {
                const elems = cmd.elements();
                undoContext_.getCanvas().pathActions.clear();
                undoContext_.call('changed', elems);
                const cmdType = cmd.type();
                const isApply = (eventType === EventTypes.AFTER_APPLY);
                if (cmdType === 'MoveElementCommand') {
                    const mecmd = cmd as MoveElementCommand;
                    const parent = isApply ? mecmd.newParent : mecmd.oldParent;
                    if (parent === undoContext_.getSVGContent()) {
                        draw.identifyLayers();
                    }
                } else if (cmdType === 'InsertElementCommand' || cmdType === 'RemoveElementCommand') {
                    if ((cmd as InsertElementCommand).parent === undoContext_.getSVGContent()) {
                        draw.identifyLayers();
                    }
                    if (cmdType === 'InsertElementCommand') {
                        if (isApply) {
                            undoContext_.restoreRefElems(cmd.elem);
                        }
                    } else if (!isApply) {
                        undoContext_.restoreRefElems(cmd.elem);
                    }
                    if (cmd.elem && cmd.elem.tagName === 'use') {
                        undoContext_.getCanvas().setUseData(cmd.elem);
                    }
                } else if (cmdType === 'ChangeElementCommand') {
                    // if we are changing layer names, re-identify all layers
                    const cecmd = cmd as ChangeElementCommand;
                    if (cecmd.elem.tagName === 'title' &&
                        cecmd.elem.parentNode.parentNode === undoContext_.getSVGContent()
                    ) {
                        draw.identifyLayers();
                    }
                    const values = isApply ? cecmd.newValues : cecmd.oldValues;
                    // If stdDeviation was changed, update the blur.
                    if (values.stdDeviation) {
                        undoContext_.getCanvas().setBlurOffsets(cecmd.elem.parentNode, values.stdDeviation);
                    }
                    // This is resolved in later versions of webkit, perhaps we should
                    // have a featured detection for correct 'use' behavior?
                    // ——————————
                    // Remove & Re-add hack for Webkit (issue 775)
                    // if (cmd.elem.tagName === 'use' && isWebkit()) {
                    //  const {elem} = cmd;
                    //  if (!elem.getAttribute('x') && !elem.getAttribute('y')) {
                    //    const parent = elem.parentNode;
                    //    const sib = elem.nextSibling;
                    //    elem.remove();
                    //    parent.insertBefore(elem, sib);
                    //    // Ok to replace above with this? `sib.before(elem);`
                    //  }
                    // }
                }
            }
        }
    });
};

/**
* Hack for Firefox bugs where text element features aren't updated or get
* messed up. See issue 136 and issue 137.
* This function clones the element and re-selects it.
* @function module:svgcanvas~ffClone
* @todo Test for this bug on load and add it to "support" object instead of
* browser sniffing
* @param {Element} elem - The (text) DOM element to clone
* @returns {Element} Cloned element
*/
export const ffClone = function <T extends Element = Element>(elem: T): T {
    if (!isGecko()) { return elem; }
    const clone = elem.cloneNode(true) as T;
    elem.before(clone);
    elem.remove();

    undoContext_.getCanvas().selectorManager.releaseSelector(elem);
    undoContext_.getCanvas().setSelectedElements(0, clone);
    undoContext_.getCanvas().selectorManager.requestSelector(clone).showGrips(true);

    return clone;
};

/**
 * Rebuilds the child <tspan> elements of a SVG <text> element using the line
 * positions from a pe-layout value. Each line becomes a single <tspan> with x/y
 * attributes so multi-line text wraps correctly on the canvas.
 *
 * line.y from getTextLayoutDetails is in HTML-editor-pixel space (effectiveLineHeight
 * is the raw C# multiplier, e.g. 1.0, not pixels) and MUST NOT be used here for SVG
 * positioning. Instead, y is computed from:
 *   - the <text> element's own SVG y attribute  (baseline of line 0, SVG user units)
 *   - the font-size attribute converted to SVG user units via SVGLength
 *   - the pe-leading attribute (the authoritative unitless line-height multiplier)
 *
 * Similarly, tspan x is always the <text> element's SVG x attribute; text-anchor
 * handles left/center/right alignment, so per-line x offsets are not needed.
 *
 * Layout-managed tspans carry a `data-layout-line` attribute so they can be
 * found and replaced on the next call without touching any other children.
 */
export const applyLayoutLinesToTextElement = function (textEl: SVGElement, layout: TextLayoutResult): void {
    // Remove only the tspans this function previously inserted
    for (const existing of Array.from(textEl.querySelectorAll('tspan[data-layout-line]'))) {
        existing.remove();
    }

    // Remove bare text nodes left behind by the #text assignment that wipes textContent
    for (const node of Array.from(textEl.childNodes)) {
        if (node.nodeType === Node.TEXT_NODE) {
            textEl.removeChild(node);
        }
    }

    const lines = layout?.svg?.lines;
    if (!lines || lines.length === 0) {
        return;
    }

    if (layout.svg?.textAnchor) {
        textEl.setAttribute('text-anchor', layout.svg.textAnchor);
    }

    // --- Compute positions in SVG user-coordinate space ---

    // The tspan x is the same anchor point as the <text> element itself.
    // text-anchor on the <text> element handles left/center/right alignment at this x;
    // we must NOT derive x from the editor's pixel-space resolveX values.
    const textAnchorX = parseFloat(textEl.getAttribute('x') ?? '') || 0;

    // The <text> y is the baseline of the first line, in SVG user units.
    const textBaselineY = parseFloat(textEl.getAttribute('y') ?? '') || 0;

    // Convert the font-size attribute (e.g. "12pt", "16px") to SVG user units using
    // SVGLength so that pt/px/mm are all handled correctly regardless of canvas baseUnit.
    let fontSizeInUserUnits = 12; // safe fallback
    try {
        const svgRoot = textEl.ownerSVGElement;
        if (svgRoot) {
            const svgLen = svgRoot.createSVGLength();
            svgLen.valueAsString = textEl.getAttribute('font-size') ?? '12pt';
            fontSizeInUserUnits = svgLen.value;
        }
    } catch (_e) {
        // Fallback: treat as bare user-unit number
        fontSizeInUserUnits = parseFloat(textEl.getAttribute('font-size') ?? '12') || 12;
    }

    // pe-leading is the authoritative unitless line-height multiplier set by the editor
    // (e.g. 1.0 = single spacing, 1.5 = 1.5x). Use it in preference to layout.lineHeight
    // which is the raw C# _lineheight value passed through getTextLayoutDetails and is
    // equal to the multiplier only by coincidence (effectiveLineHeight short-circuits
    // the px conversion when > 0).
    const peLeadingAttr = parseFloat(textEl.getAttribute('pe-leading') ?? '');
    const lineHeightMultiplier = Number.isFinite(peLeadingAttr) && peLeadingAttr > 0
        ? peLeadingAttr
        : 1.2;

    // Line spacing in SVG user units: font size × multiplier
    // e.g. 12pt ≈ 4.23mm, × 1.5 = 6.35mm per line on a mm-unit canvas
    const lineSpacing = fontSizeInUserUnits * lineHeightMultiplier;

    for (const line of lines) {
        const tspan = document.createElementNS(NS.SVG, 'tspan');

        // All tspans share the same x anchor; text-anchor controls alignment
        tspan.setAttribute('x', String(textAnchorX));

        // Absolute baseline y: first line sits at the <text> element's own y,
        // each subsequent line advances by one line-spacing interval
        tspan.setAttribute('y', String(textBaselineY + line.index * lineSpacing));

        tspan.setAttribute('data-layout-line', String(line.index));
        tspan.textContent = line.text;
        textEl.appendChild(tspan);
    }
};

/**
* This function makes the changes to the elements. It does not add the change
* to the history stack.
* @param {string} attr - Attribute name
* @param {string|Float} newValue - String or number with the new attribute value
* @param {Element[]} elems - The DOM elements to apply the change to
* @returns {void}
*/
export const changeSelectedAttributeNoUndoMethod = function (attr: string, pNewValue: CommandAttributeValue, elems?: SVGGraphicsElement[]) {
    const selectedElements = undoContext_.getSelectedElements();
    const currentZoom = undoContext_.getCurrentZoom();

    const newValue = Array.isArray(pNewValue) ? '' : pNewValue?.toString() ?? '';

    if (undoContext_.getCurrentMode() === 'pathedit') {
        // Editing node
        undoContext_.getCanvas().pathActions.moveNode(attr, newValue);
    }

    elems = elems || selectedElements;
    elems = getUnlockedElements(elems);
    if (elems.length === 0) {
        return;
    }

    let i = elems.length;
    const noXYElems = ['g', 'polyline', 'path'];
    // const goodGAttrs = ['transform', 'opacity', 'filter'];

    let ns: string | null = null;
    if (attr.startsWith('xml:')) {
        ns = NS.XML;
    } else if (attr.startsWith('xlink:')) {
        ns = NS.XLINK;
    } else if (attr.startsWith('zaa:')) {
        ns = NS.ZAA;
    }

    while (i--) {
        let elem = elems[i];
        if (isNullish(elem)) {
            continue;
        }

        if (attr === '#children') {
            replaceChildren(elem, pNewValue);
            continue;
        }

        // Set x,y vals on elements that don't have them
        if ((attr === 'x' || attr === 'y') && noXYElems.includes(elem.tagName)) {
            const bbox = getStrokedBBoxDefaultVisible([elem]);
            const diffX = attr === 'x' ? parseFloat(newValue) - bbox.x : 0;
            const diffY = attr === 'y' ? parseFloat(newValue) - bbox.y : 0;
            undoContext_.getCanvas().moveSelectedElements(diffX * currentZoom, diffY * currentZoom, true);
            continue;
        }

        // only allow the transform/opacity/filter attribute to change on <g> elements, slightly hacky
        // TODO: Missing statement body
        // if (elem.tagName === 'g' && goodGAttrs.includes(attr)) {}
        let oldval = attr === '#text' ? elem.textContent : elem.getAttribute(attr);
        if (isNullish(oldval)) { oldval = ''; }
        if (oldval !== newValue) {
            if (attr === '#text') {
                // const oldW = utilsGetBBox(elem).width;
                elem.textContent = newValue;

                // FF bug occurs on on rotated elements
                if ((/rotate/).test(elem.getAttribute('transform'))) {
                    elem = ffClone(elem);
                }
                // Hoped to solve the issue of moving text with text-anchor="start",
                // but this doesn't actually fix it. Hopefully on the right track, though. -Fyrd
                // const box = getBBox(elem), left = box.x, top = box.y, {width, height} = box,
                //   dx = width - oldW, dy = 0;
                // const angle = getRotationAngle(elem, true);
                // if (angle) {
                //   const r = Math.sqrt(dx * dx + dy * dy);
                //   const theta = Math.atan2(dy, dx) - angle;
                //   dx = r * Math.cos(theta);
                //   dy = r * Math.sin(theta);
                //
                //   elem.setAttribute('x', elem.getAttribute('x') - dx);
                //   elem.setAttribute('y', elem.getAttribute('y') - dy);
                // }
            } else if (attr === '#href') {
                setHref(elem, newValue);
            } else if (attr === 'pe-layout') {
                // Store the JSON as a regular SVG attribute so it round-trips on save/load
                if (newValue) {
                    elem.setAttribute('pe-layout', newValue);
                } else {
                    elem.removeAttribute('pe-layout');
                }

                // Rebuild <tspan> children from layout lines on <text> elements.
                // This branch is always processed AFTER the #text branch (guaranteed by the
                // sort in changeSelectedAttributesMethod) so the textContent wipe has
                // already happened before we insert the new tspans.
                if (newValue && elem.tagName.toLowerCase() === 'text') {
                    try {
                        const layout: TextLayoutResult = JSON.parse(newValue);
                        applyLayoutLinesToTextElement(elem, layout);
                    } catch (e) {
                        console.warn('pe-layout: failed to parse or apply layout', e);
                    }
                }
            } else if (newValue) {
                if(ns) {
                    elem.removeAttribute(attr); // Remove non-namespaced version first if any.
                    elem.setAttributeNS(ns, attr, newValue);
                } else {
                    elem.setAttribute(attr, newValue);
                }
            } else {
                if(ns) {
                    elem.removeAttributeNS(ns, attr);
                }
                
                elem.removeAttribute(attr);
            }

            // Go into "select" mode for text changes
            // NOTE: Important that this happens AFTER elem.setAttribute() or else attributes like
            // font-size can get reset to their old value, ultimately by svgEditor.updateContextPanel(),
            // after calling textActions.toSelectMode() below
            if (undoContext_.getCurrentMode() === 'textedit' && attr !== '#text' && elem.textContent.length) {
                undoContext_.getCanvas().textActions.toSelectMode(elem);
            }

            // if (i === 0) {
            //   selectedBBoxes[0] = utilsGetBBox(elem);
            // }

            // Use the Firefox ffClone hack for text elements with gradients or
            // where other text attributes are changed.
            if (isGecko() &&
                elem.nodeName === 'text' &&
                (/rotate/).test(elem.getAttribute('transform')) &&
                (String(newValue).startsWith('url') || (['font-size', 'font-family', 'x', 'y'].includes(attr) && elem.textContent))) {
                elem = ffClone(elem);
            }

            // Timeout needed for Opera & Firefox
            if (selectedElements.includes(elem)) {
                // eslint-disable-next-line no-loop-func
                setTimeout(() => {
                    // Due to element replacement, this element may no longer
                    // be part of the DOM
                    const currentSelectedElements = undoContext_.getSelectedElements();

                    if (!elem.parentNode || !currentSelectedElements.includes(elem)) {
                        return;
                    }

                    undoContext_.getCanvas().selectorManager.requestSelector(elem).resize();
                }, 0);
            }

            // if this element was rotated, and we changed the position of this element
            // we need to update the rotational transform attribute
            const angle = getRotationAngle(elem);
            if (angle !== 0 && attr !== 'transform') {
                const tlist = getTransformList(elem);
                let n = tlist.numberOfItems;
                while (n--) {
                    const xform = tlist.getItem(n);
                    if (xform.type === 4) {
                        // remove old rotate
                        tlist.removeItem(n);

                        const box = utilsGetBBox(elem);
                        const center = transformPoint(
                            box.x + box.width / 2, box.y + box.height / 2, transformListToTransform(tlist as any).matrix // TODO: Fix as any
                        );
                        const cx = center.x,
                            cy = center.y;
                        const newrot = undoContext_.getSVGRoot().createSVGTransform();
                        newrot.setRotate(angle, cx, cy);
                        tlist.insertItemBefore(newrot, n);
                        break;
                    }
                }
            }
        } // if oldValue != newValue
    } // for each elem

    setTimeout(() => SvgAnnotations.UpdateAnnotations());
};

/**
* Change the given/selected element and add the original value to the history stack.
* If you want to change all `selectedElements`, ignore the `elems` argument.
* If you want to change only a subset of `selectedElements`, then send the
* subset to this function in the `elems` argument.
* @function module:svgcanvas.SvgCanvas#changeSelectedAttribute
* @param {string} attr - String with the attribute name
* @param {string|Float} val - String or number with the new attribute value
* @param {Element[]} elems - The DOM elements to apply the change to
* @returns {void}
*/
export const changeSelectedAttributeMethod = function (attr: string, val: string | number | boolean | null, elems?: SVGGraphicsElement[]) {
    changeSelectedAttributesMethod({ [attr]: val?.toString() }, elems);
};

/**
* Change the given/selected element and add the original value to the history stack.
* If you want to change all `selectedElements`, ignore the `elems` argument.
* If you want to change only a subset of `selectedElements`, then send the
* subset to this function in the `elems` argument.
* @function module:svgcanvas.SvgCanvas#changeSelectedAttribute
* @param {string} attr - String with the attribute name
* @param {string|Float} val - String or number with the new attribute value
* @param {Element[]} elems - The DOM elements to apply the change to
* @returns {void}
*/
export const changeSelectedAttributesMethod = function (attrValMap: CommandAttributes, elems?: SVGGraphicsElement[] | string[]) {
    if (!elems) {
        elems = undoContext_.getSelectedElements();
    } else if (typeof elems[0] === 'string') {
        const ids = elems as string[];
        elems = ids.map(id => document.getElementById(id) as unknown as SVGGraphicsElement).filter(el => el != null);
    }

    // Sort so that #text (which wipes textContent/children) is processed before pe-layout
    // (which rebuilds tspan children). All other attributes retain their original order.
    const processingOrder = (key: string): number => {
        if (key === 'pe-layout') return 3;
        if (key === '#children') return 2;
        if (key === '#text') return 1;
        return 0;
    };

    elems = getUnlockedElements(elems as SVGGraphicsElement[]);
    const attValMapEntries = Object.entries(attrValMap)
        .filter(([attr]) => attr !== '#type')
        .sort(([a], [b]) => processingOrder(a) - processingOrder(b));

    if (elems.length === 0 || attValMapEntries.length === 0) {
        return;
    }

    const changes = {};
    const originalChildren = elems.length === 1 && attValMapEntries.some(([attr]) => attr === '#children')
        ? serializeChildren(elems[0])
        : null;
    for (const [attr, val] of attValMapEntries) {
        if (elems.length > 1) {
            undoContext_.getCanvas().undoMgr.beginUndoableChange(attr, elems);
        } else if (attr === '#text') {
            changes[attr] = elems[0].textContent;
        } else if (attr === '#href') {
            changes[attr] = getHref(elems[0]);
        } else if (attr === '#children') {
            changes[attr] = originalChildren;
        } else {
            changes[attr] = elems[0].getAttribute(attr);
        }

        changeSelectedAttributeNoUndoMethod(attr, val, elems);

        if (elems.length > 1) {
            const batchCmd = undoContext_.getCanvas().undoMgr.finishUndoableChange();
            if (!batchCmd.isEmpty()) {
                undoContext_.getCanvas().undoMgr.addCommandToHistory(batchCmd);
            }
        }
    }

    if (elems.length === 1) {
        let changeMessage: string;
        if (attValMapEntries.length === 1) {
            changeMessage = `Change ${attValMapEntries[0][0]}`;
        } else {
            changeMessage = `Change ${attValMapEntries.length} attributes`;
        }
        undoContext_.getCanvas().undoMgr.addCommandToHistory(new ChangeElementCommand(elems[0], changes, changeMessage));
    }

    svgEditServiceInstance.ElementStateHasChanged(elems);
};

export const bulkChangeAttributesMethod = function (mappings: { [key: string]: CommandAttributes }) {
    for (const [id, attrValMap] of Object.entries(mappings)) {
        const elem = document.getElementById(id) as unknown as SVGGraphicsElement;
        if (elem) {
            changeSelectedAttributesMethod(attrValMap, [elem]);
        }
    }
};
